/**
 * Billing types.
 *
 * Two rules from `BUSINESS_RULES.md` shape everything here, and both are structural rather
 * than cosmetic:
 *
 * 1. **Paid credit balances always carry over** on upgrade AND downgrade. They were sold as
 *    never-expiring, so a plan change must not void them. The monthly allowance is different:
 *    it resets with the billing period and is NOT credited forward.
 *
 * 2. **Proration and credit carry-over are two independent calculations and are never netted
 *    against each other.** The pro-rated *money* adjustment on the subscription fee and the
 *    carry-over of *paid credit balances* are separate facts, shown as separate lines. This is
 *    why `PlanChange` below keeps them in different shapes entirely — one is `money`, the
 *    other is `carriedBalances`. They cannot accidentally be summed.
 *
 * The vendor is never named in customer-facing UI, so nothing here carries a provider name.
 */
import type { CurrencyCode } from '@/lib/pricing';
import type { Tier } from '@/lib/tier';

/**
 * Paid, purchased balances — top-up packs. Never the monthly allowance.
 *
 * A field is `null` when the customer holds no balance of that kind. That is deliberately
 * distinct from `0`: "no balance" means the carry-over line does not render at all, while a
 * zero balance would render a line saying nothing carries over. The design requires the line
 * to be absent, so the data layer has to preserve the difference.
 */
export interface PaidBalances {
  /** Ask Aware — measured in questions. */
  askAwareQuestions: number | null;
  /** Aware Explain — measured in uses. */
  awareExplainUses: number | null;
}

export function hasAnyBalance(balances: PaidBalances): boolean {
  return balances.askAwareQuestions !== null || balances.awareExplainUses !== null;
}

/**
 * The money side of a plan change. Computed server-side from the unused portion of the current
 * period at the OLD plan's price — never in the browser, which cannot be trusted with an
 * invoice and does not know the true period boundaries.
 */
export interface Proration {
  /** Credit for the unused part of the current period. Positive number, shown as a negative. */
  unusedCredit: number;
  /** The new plan's cost for the days remaining in the period. */
  remainderCharge: number;
  /**
   * What is actually taken today. Combining the two figures above is just invoice arithmetic
   * and is allowed — the rule against netting applies to money-versus-balances, not to the
   * proration itself.
   */
  dueToday: number;
  /** Where a downgrade leaves more credit than charge, it rolls forward instead of refunding. */
  rollsToNextInvoice: number;
}

export type PlanChangeKind = 'upgrade' | 'downgrade';

export interface PlanChange {
  kind: PlanChangeKind;
  fromTier: Tier;
  toTier: Tier;
  currency: CurrencyCode;
  /** Monthly or annual — the new plan's recurring price. */
  recurringPrice: number;
  billingPeriod: 'monthly' | 'annual';
  /** mm/dd/yyyy — when the next full charge lands. */
  nextChargeDate: string;
  /**
   * Money. Null for a customer still in their trial: nothing has been charged, so there is
   * nothing to pro-rate. That case gets a plain statement instead of an adjustment table.
   */
  money: Proration | null;
  /** Balances that carry to the new plan. Independent of `money` — never combined with it. */
  carriedBalances: PaidBalances;
  /** True while the 14-day trial is still running. */
  inTrial: boolean;
  /** For a trial customer: the exact amount of the first charge, on `nextChargeDate`. */
  firstChargeAmount: number | null;
}

/** A purchasable top-up pack. Balances bought this way never expire. */
export interface TopUpPack {
  id: string;
  /** Which meter it fills. */
  meter: 'askAware' | 'awareExplain';
  /** e.g. 100 questions, 500 uses. */
  quantity: number;
  price: number;
  currency: CurrencyCode;
}

export const METER_LABEL: Record<TopUpPack['meter'], { name: string; unit: string }> = {
  // "questions" for Ask Aware, "uses" for Aware Explain. Never "tokens", never "credits" as a
  // generic term — "credit" is reserved for the money line.
  askAware: { name: 'Ask Aware', unit: 'questions' },
  awareExplain: { name: 'Aware Explain', unit: 'uses' },
};

/** Usage against the monthly allowance. Separate from paid balances, and reset each period. */
export interface MeterUsage {
  used: number;
  /** The monthly allowance. Null where the plan does not include this meter at all. */
  allowance: number | null;
  /** Purchased balance sitting on top of the allowance. Null when none is held. */
  paidBalance: number | null;
}

export interface AccountState {
  /**
   * Null for a signed-in user who has never subscribed. The API answers this route even
   * without a subscription — the plan chooser needs it — so callers must handle the null
   * rather than assume a tier is always present.
   */
  tier: Tier | null;
  billingPeriod: 'monthly' | 'annual';
  currency: CurrencyCode;
  /** mm/dd/yyyy */
  nextChargeDate: string;
  /**
   * Null until a billing event has named the price this subscription is bound to. Callers must
   * omit the figure rather than substitute 0 — "$0.00 next charge" reads as a promise, and it
   * is not one the billing system has made.
   */
  nextChargeAmount: number | null;
  inTrial: boolean;
  /** mm/dd/yyyy — the day the trial converts, stated everywhere the trial is mentioned. */
  trialEndsOn: string | null;
  cardOnFile: boolean;
  awareExplain: MeterUsage;
  /** Null where the plan does not include Ask Aware. */
  askAware: MeterUsage | null;
  emailAddress: string;
  whatsAppNumber: string | null;
  /** Set once cancellation is confirmed — access continues until this date. */
  accessEndsOn: string | null;
}
